import { useState, useEffect, useCallback, useRef } from 'react';
import { api, ChatChannelResponse } from '@/lib/api';

/** Must match GLOBAL_CHANNEL_ID in @fias/db-types */
const GLOBAL_CHANNEL_ID = 'cch_global_fias_community';

interface UseChatChannelsReturn {
  channels: ChatChannelResponse[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
  createChannel: (input: {
    channelType: 'group' | 'guild';
    name: string;
    description?: string;
  }) => Promise<ChatChannelResponse | null>;
  openDirectMessage: (targetUserId: string) => Promise<ChatChannelResponse | null>;
}

export function useChatChannels(): UseChatChannelsReturn {
  const [channels, setChannels] = useState<ChatChannelResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const hasAutoJoinedGlobal = useRef(false);

  const fetchChannels = useCallback(async () => {
    try {
      const result = await api.listChatChannels();
      setChannels(result.channels);
      return result.channels;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load channels');
      return [];
    }
  }, []);

  const ensureGlobalChannel = useCallback(
    async (currentChannels: ChatChannelResponse[]) => {
      if (hasAutoJoinedGlobal.current) return;
      hasAutoJoinedGlobal.current = true;

      const hasGlobal = currentChannels.some((c) => c.channelId === GLOBAL_CHANNEL_ID);
      if (hasGlobal) return;

      try {
        await api.joinChatChannel(GLOBAL_CHANNEL_ID);
        await fetchChannels();
      } catch {
        // Global channel may not exist yet (migration not run) — ignore
      }
    },
    [fetchChannels],
  );

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    const result = await fetchChannels();
    setLoading(false);
    await ensureGlobalChannel(result);
  }, [fetchChannels, ensureGlobalChannel]);

  const createChannel = useCallback(
    async (input: {
      channelType: 'group' | 'guild';
      name: string;
      description?: string;
    }): Promise<ChatChannelResponse | null> => {
      try {
        const result = await api.createChatChannel(input);
        await fetchChannels();
        return result.channel;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to create channel';
        setError(message);
        throw new Error(message);
      }
    },
    [fetchChannels],
  );

  const openDirectMessage = useCallback(
    async (targetUserId: string): Promise<ChatChannelResponse | null> => {
      try {
        const result = await api.getOrCreateDirectChannel(targetUserId);
        await fetchChannels();
        return result.channel;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to open direct message';
        setError(message);
        throw new Error(message);
      }
    },
    [fetchChannels],
  );

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { channels, loading, error, refresh, createChannel, openDirectMessage };
}
